/**
 * "Arrange by group": an action (never a switch) that tidies the class diagram group by group,
 * like sorting sticky notes into frames on a Miro board.
 *
 * - Each group's boxes are gathered into one block: the boxes in the group itself laid out in
 *   layers among themselves (base types on top), its sub-groups as blocks beside them, the
 *   whole wrapped by the package's frame.
 * - The blocks of one level (and the boxes in no group, as one block of their own) are packed
 *   side by side in rows, in the graph's order, so the result is compact whatever the links.
 * - The result starts where the arranged boxes start now (the view does not jump) and is
 *   **written as positions** (`update_node` with `diagram`), so the boxes are free afterwards:
 *   one `graph.batch`, one undo, seen by everyone.
 *
 * What it arranges follows the selection: nothing selected → the whole diagram; a package, or
 * one box → that package (one box in no group → the whole diagram); several boxes → those.
 * Boxes the filters hide are arranged with their group, like the others (filters never move a
 * box, so they come back in their group's block).
 *
 * Pure, so the result is the same on every page and can be tested without a page.
 */
import type { Graph } from "@protocol/Graph";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { Node } from "@protocol/Node";
import type { Selection } from "../view";
import type { BoxShape } from "./boxes";
import { layoutDiagram, type BoxPositions, type DiagramFilters, type DiagramLayout } from "./layout";
import { COLUMN_GAP, PACKAGE_PAD, PACKAGE_TAB, ROW_GAP } from "./constants";

/** What an arrangement applies to. */
export type ArrangeScope = { type: "all" } | { type: "boxes"; ids: string[] } | { type: "package"; id: string };

/** The scope "Arrange by group" has with this selection in this graph. */
export function arrangeScope(selection: Selection, graph: Pick<Graph, "nodes" | "groups">): ArrangeScope {
  if (selection?.type === "nodes") return { type: "boxes", ids: selection.ids };
  if (selection?.type === "group") return { type: "package", id: selection.id };
  if (selection?.type === "node") {
    const group = graph.nodes.find((n) => n.id === selection.id)?.group;
    if (group && graph.groups.some((g) => g.id === group)) return { type: "package", id: group };
  }
  return { type: "all" };
}

/** Says what the action does, for its button and menu. */
export function arrangeLabel(scope: ArrangeScope, graph: Pick<Graph, "groups">): string {
  if (scope.type === "boxes") return `Arrange the ${scope.ids.length} selected boxes by group`;
  if (scope.type === "package") return `Arrange the package ${graph.groups.find((g) => g.id === scope.id)?.name ?? scope.id} by group`;
  return "Arrange the whole diagram by group";
}

/** Every filter open (positions never depend on the filters); groups are blocks of their own here. */
const PLAIN: DiagramFilters = { hiddenCategories: [], linkKinds: { inherits: true, implements: true, uses: true, depends: true }, groups: false, bundles: false };

/** A laid-out block: its size and where each box goes inside it (top-left corners). */
interface Block {
  w: number;
  h: number;
  at: BoxPositions;
}

/** The nodes a scope arranges. */
function idsOf(graph: Graph, scope: ArrangeScope): string[] {
  if (scope.type === "all") return graph.nodes.map((n) => n.id);
  if (scope.type === "boxes") return scope.ids.filter((id) => graph.nodes.some((n) => n.id === id));
  return graph.nodes.filter((n) => within(graph, n.group, scope.id)).map((n) => n.id);
}

/** Whether group `group` is `id` or inside it. */
function within(graph: Pick<Graph, "groups">, group: string | null, id: string): boolean {
  for (let at = group, steps = 0; at && steps < 1000; at = graph.groups.find((g) => g.id === at)?.parent ?? null, steps++) if (at === id) return true;
  return false;
}

/** Where the boxes of `ids` go, arranged by group, relative to the whole block's corner. */
export function arrangeByGroup(graph: Graph, ids: ReadonlySet<string>, shapeOf: (node: Node) => BoxShape): Block {
  const groups = new Map(graph.groups.map((g) => [g.id, g]));
  /** The group a node is in (`null`: none, or one that does not exist). */
  const groupOf = (n: Node) => (n.group && groups.has(n.group) ? n.group : null);
  const parentOf = (id: string) => {
    const parent = groups.get(id)?.parent ?? null;
    return parent && groups.has(parent) && parent !== id ? parent : null;
  };
  const nodes = graph.nodes.filter((n) => ids.has(n.id));
  // The groups holding something arranged, with what is directly in each.
  const used = new Set<string>();
  for (const n of nodes) for (let g = groupOf(n), steps = 0; g && !used.has(g) && steps < 1000; g = parentOf(g), steps++) used.add(g);
  const boxesIn = (container: string | null) => nodes.filter((n) => groupOf(n) === container);
  const groupsIn = (container: string | null) => graph.groups.filter((g) => used.has(g.id) && parentOf(g.id) === container).map((g) => g.id);

  /** The boxes directly in a container, in layers among themselves. */
  const loose = (container: string | null): Block | null => {
    const members = boxesIn(container);
    if (!members.length) return null;
    const own = new Set(members.map((n) => n.id));
    const part: Graph = {
      nodes: members.map(({ diagram: _, group: __, ...n }) => ({ ...n, group: null })),
      links: graph.links.filter((l) => own.has(l.from) && own.has(l.to)),
      groups: [],
    };
    const layout = layoutDiagram(part, PLAIN, {}, shapeOf);
    const x0 = Math.min(...layout.boxes.map((b) => b.x));
    const y0 = Math.min(...layout.boxes.map((b) => b.y));
    const at: BoxPositions = {};
    for (const b of layout.boxes) at[b.id] = [b.x - x0, b.y - y0];
    return { w: Math.max(...layout.boxes.map((b) => b.x + b.w)) - x0, h: Math.max(...layout.boxes.map((b) => b.y + b.h)) - y0, at };
  };

  /** A container's content: its loose boxes first, then its sub-groups, packed in rows. */
  const content = (container: string | null): Block => {
    const parts: Block[] = [];
    const own = loose(container);
    if (own) parts.push(own);
    for (const g of groupsIn(container)) {
      const inner = content(g);
      // The package's frame around its content: padding all round, the name tab on top.
      const at: BoxPositions = {};
      for (const [id, [x, y]] of Object.entries(inner.at)) at[id] = [x + PACKAGE_PAD, y + PACKAGE_TAB + PACKAGE_PAD];
      parts.push({ w: inner.w + 2 * PACKAGE_PAD, h: inner.h + PACKAGE_TAB + 2 * PACKAGE_PAD, at });
    }
    return pack(parts);
  };
  return content(null);
}

/** Packs blocks side by side in rows (shelves), about as wide as tall overall. */
function pack(parts: Block[]): Block {
  if (!parts.length) return { w: 0, h: 0, at: {} };
  const area = parts.reduce((sum, p) => sum + (p.w + COLUMN_GAP) * (p.h + ROW_GAP), 0);
  const limit = Math.max(...parts.map((p) => p.w), Math.sqrt(area) * 1.4);
  const at: BoxPositions = {};
  let [x, y, row, w] = [0, 0, 0, 0];
  for (const p of parts) {
    if (x > 0 && x + p.w > limit) {
      y += row + ROW_GAP;
      [x, row] = [0, 0];
    }
    for (const [id, [bx, by]] of Object.entries(p.at)) at[id] = [x + bx, y + by];
    w = Math.max(w, x + p.w);
    row = Math.max(row, p.h);
    x += p.w + COLUMN_GAP;
  }
  return { w, h: y + row, at };
}

/**
 * The edits arranging `scope` by group, from the diagram as it is now (`layout`): a position
 * for every box that moves. When nobody placed any box yet, the boxes left out are fixed where
 * they are drawn in the same edit, so nothing else jumps. None when nothing would move.
 */
export function arrangeEdits(graph: Graph, layout: DiagramLayout, scope: ArrangeScope, shapeOf: (node: Node) => BoxShape): GraphEdit[] {
  const ids = new Set(idsOf(graph, scope));
  if (!ids.size) return [];
  const block = arrangeByGroup(graph, ids, shapeOf);
  // Starts where the arranged boxes start now.
  const now = [...ids].map((id) => layout.positions[id]!);
  const [ox, oy] = [Math.min(...now.map((p) => p[0])), Math.min(...now.map((p) => p[1]))];
  // The boxes' corner, not the frames' (a package's padding is already around them).
  const inside = Object.values(block.at);
  const [bx, by] = [Math.min(...inside.map((p) => p[0])), Math.min(...inside.map((p) => p[1]))];
  const target: BoxPositions = {};
  for (const id of ids) target[id] = [Math.round(block.at[id]![0] - bx + ox), Math.round(block.at[id]![1] - by + oy)];
  const moves = (id: string) => {
    const at = layout.positions[id]!;
    return Math.round(at[0]) !== target[id]![0] || Math.round(at[1]) !== target[id]![1];
  };
  if (![...ids].some(moves)) return [];
  const placed = new Map(graph.nodes.map((n) => [n.id, n.diagram]));
  if (![...placed.values()].some(Boolean))
    for (const n of graph.nodes) if (!ids.has(n.id)) target[n.id] = [Math.round(layout.positions[n.id]![0]), Math.round(layout.positions[n.id]![1])];
  return Object.entries(target)
    .filter(([id, at]) => {
      const was = placed.get(id);
      return !was || was[0] !== at[0] || was[1] !== at[1];
    })
    .map(([id, at]) => ({ edit: "update_node", id, patch: { diagram: at } }));
}
